import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  TextInput,
  toast,
} from 'component-library';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components';
import { usePostNdiCreditsAdjustment } from '@/hooks/useNdiCredits';

type AddCreditsFormValues = {
  amount: string;
  description: string;
};

type AddCreditsDialogProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
};

export const AddCreditsDialog = ({ open, onClose, organizationId }: AddCreditsDialogProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const postMutation = usePostNdiCreditsAdjustment();

  const form = useForm<AddCreditsFormValues>({
    defaultValues: { amount: '', description: '' },
  });

  const handleClose = () => {
    form.reset({ amount: '', description: '' });
    onClose();
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await postMutation.mutateAsync({
        queryParams: { org: organizationId },
        bodyData: { amount: values.amount, description: values.description },
      });

      queryClient.invalidateQueries({ queryKey: ['ndi-credits-balance', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['ndi-credits-transactions', organizationId] });
      toast({ type: 'success', title: t('shared.ndiOrganizationSubscriptions.financialPage.toast.addCreditsSuccess') });
      handleClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiOrganizationSubscriptions.financialPage.toast.addCreditsError') });
    }
  });

  const requiredLabel = t('global.ui.form.input.required');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className='min-w-[30vw]'>
        <DialogHeader>
          <DialogTitle>{t('shared.ndiOrganizationSubscriptions.financialPage.addCreditsDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form {...form}>
            <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
              <FormField
                control={form.control}
                name='amount'
                rules={{
                  required: requiredLabel,
                  validate: (v) =>
                    Number.isFinite(Number(v)) || t('shared.ndiOrganizationSubscriptions.formRules.invalidNumber'),
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('shared.ndiOrganizationSubscriptions.financialPage.addCreditsDialog.amount')} *
                    </FormLabel>
                    <FormControl>
                      <TextInput {...field} inputMode='numeric' />
                    </FormControl>
                    <span className='typography-caption text-text-secondary'>
                      {t('shared.ndiOrganizationSubscriptions.financialPage.addCreditsDialog.amountHelp')}
                    </span>
                    <FormMessage>{form.formState.errors.amount?.message}</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='description'
                rules={{ required: requiredLabel }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('shared.ndiOrganizationSubscriptions.financialPage.addCreditsDialog.description')} *
                    </FormLabel>
                    <FormControl>
                      <textarea
                        {...field}
                        className='flex min-h-[100px] w-full rounded-xl border border-neutral-black-12 px-4 py-4 placeholder:text-text-disabled resize-none focus:border-text-primary focus:outline-none'
                      />
                    </FormControl>
                    <FormMessage>{form.formState.errors.description?.message}</FormMessage>
                  </FormItem>
                )}
              />
              <Button
                type='submit'
                className='self-end w-fit px-6'
                loading={postMutation.isPending}
                disabled={postMutation.isPending}
              >
                {t('global.ui.buttons.save')}
              </Button>
            </form>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
